/**
 * Per-record SQL views for the insight cards to come.
 *
 * One view per record, named from the record id, exposing the materialized
 * columns plus one json_extract column per field. A card then queries
 * `v_<record>` with plain WHERE clauses instead of reaching into values_json
 * itself. Pending rows are included by default — the `pending` column is
 * exposed so a card may exclude them, and `deleted_at` so it may honor
 * tombstones.
 *
 * The statement builders are pure and tested without a device. `rebuildView`
 * takes its runner as a parameter rather than importing db.ts: expo-sqlite
 * cannot load under bun, and a module that imports it cannot be unit tested.
 * The caller (entry-repo.ts) passes execRaw.
 *
 * Views are rebuilt, never migrated: cacheSchemas calls rebuildView whenever
 * it stores a schema whose field_set_ver changed.
 */
import type { RecordSchemaResponse } from '@alluzo/shared';

/** v_<recordId with dashes→underscores>. */
export const viewNameFor = (recordId: string): string => `v_${recordId.replaceAll('-', '_')}`;

/** f_<fieldId with dashes→underscores>. */
export const columnNameFor = (fieldId: string): string => `f_${fieldId.replaceAll('-', '_')}`;

/** A double-quoted SQL identifier. Ids are uuids; the quoting is belt and braces. */
export const quoteIdent = (identifier: string): string => `"${identifier.replaceAll('"', '""')}"`;

/**
 * A single-quoted SQL string literal.
 *
 * Exported because `entry-filter.ts` builds the same `$."<fieldId>"` JSON paths
 * against `entry_mirror` that this file bakes into the view. One quoting rule
 * for both, so a field id hostile to one is hostile to neither.
 */
export const quoteLiteral = (value: string): string => `'${value.replaceAll("'", "''")}'`;

export const dropViewSql = (recordId: string): string =>
  `DROP VIEW IF EXISTS ${quoteIdent(viewNameFor(recordId))}`;

export const createViewSql = (schema: RecordSchemaResponse): string => {
  const fieldColumns = schema.fields.map(
    (field) =>
      `json_extract(values_json, ${jsonPathFor(field.id)}) AS ${quoteIdent(columnNameFor(field.id))}`,
  );
  const columns = ['entry_id', 'day', 'signed_value', 'pending', 'deleted_at', ...fieldColumns];
  return (
    `CREATE VIEW ${quoteIdent(viewNameFor(schema.recordId))} AS ` +
    `SELECT ${columns.join(', ')} FROM entry_mirror WHERE record_id = ${quoteLiteral(schema.recordId)}`
  );
};

/** Drop and recreate the record's view. Two statements, one logical step. */
export const rebuildView = (
  schema: RecordSchemaResponse,
  run: (statement: string) => void,
): void => {
  run(dropViewSql(schema.recordId));
  run(createViewSql(schema));
};

/** The record is gone from this device; its view goes with it. */
export const dropView = (recordId: string, run: (statement: string) => void): void => {
  run(dropViewSql(recordId));
};

/* ------------------------------------------------------------ field indexes */

/**
 * The field type a person groups and filters by value across a long list:
 * "by worker", "these three sites". A select is grouped too, but it holds a
 * handful of choices, the filter sheet reads nothing for one, and indexing
 * every select would double what the indexes cost in space.
 */
const INDEXED_TYPES: ReadonlySet<string> = new Set(['reference']);

/** `ix_<record>_<field>`: one index per grouped field, named so a record's set can be found. */
export const fieldIndexNameFor = (recordId: string, fieldId: string): string =>
  `${fieldIndexPrefixFor(recordId)}${fieldId.replaceAll('-', '_')}`;

export const fieldIndexPrefixFor = (recordId: string): string => `ix_${recordId.replaceAll('-', '_')}_`;

/**
 * One index per reference field, ON THE VIEW'S OWN EXPRESSION.
 *
 * A breakdown "by worker" and a filter "these three workers" both read the
 * field through `json_extract`, and without an index SQLite parses the JSON of
 * every row of the record to answer — on a record of two lakh rows, a second
 * and a half of the JavaScript thread for the filter sheet to say how many
 * workers have been recorded. With the expression indexed, the same query is
 * answered from the index alone, in tens of milliseconds, and never opens a
 * row. The expression must be character for character what `createViewSql`
 * and the list filter write, or SQLite will not match it; `jsonPathFor` is that
 * one spelling.
 *
 * The cost is space: about 120 bytes a row for each indexed field, about
 * thirty megabytes on a record of two lakh rows. That is why only references
 * are indexed, not every field.
 */
export const fieldIndexes = (schema: RecordSchemaResponse): readonly { name: string; sql: string }[] =>
  schema.fields
    .filter((field) => INDEXED_TYPES.has(field.type))
    .map((field) => {
      const name = fieldIndexNameFor(schema.recordId, field.id);
      return {
        name,
        sql:
          `CREATE INDEX IF NOT EXISTS ${quoteIdent(name)} ON entry_mirror` +
          `(record_id, json_extract(values_json, ${jsonPathFor(field.id)})) WHERE deleted_at IS NULL`,
      };
    });

/** The one spelling of a field's JSON path, shared by the view, the filter and the indexes. */
export const jsonPathFor = (fieldId: string): string => quoteLiteral(`$."${fieldId}"`);

/**
 * The statements that move a record from the indexes it has to the ones its
 * schema wants: drop the ones no field wants, create the missing ones, and
 * leave an index that is still wanted alone — rebuilding it would re-read the
 * whole record for nothing.
 */
export const fieldIndexChanges = (
  schema: RecordSchemaResponse,
  existing: readonly string[],
): readonly string[] => {
  const wanted = fieldIndexes(schema);
  const keep = new Set(wanted.map((index) => index.name));
  const held = new Set(existing);
  return [
    ...existing.filter((name) => !keep.has(name)).map((name) => `DROP INDEX IF EXISTS ${quoteIdent(name)}`),
    ...wanted.filter((index) => !held.has(index.name)).map((index) => index.sql),
  ];
};
